import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useApp } from './store.tsx';
import { api } from './account.ts';
import { useSession } from './session.tsx';
import { withSessionLock } from './session-lock.ts';
import { effectivePlanFingerprint, baselinePlan, decidePlanSync, validateCloudPlan } from './cloud-plan.ts';

const SETTLE_MS = 4000;
export type CloudSyncStatus = 'idle' | 'pulling' | 'review' | 'pending' | 'saving' | 'saved' | 'offline' | 'failed';
interface CloudSyncValue {
  status: CloudSyncStatus;
  detail: string;
  saveNow(): Promise<boolean>;
  recheck(): void;
  accountCopy: Record<string, unknown> | null;
  savedAt: string | null;
}
const CloudSyncContext = createContext<CloudSyncValue | null>(null);

export function CloudSyncProvider({ children }: { children: ReactNode }) {
  const { state, actions, storage } = useApp();
  const { session, trusted, refresh } = useSession();
  const [status, setStatus] = useState<CloudSyncStatus>('idle');
  const [detail, setDetail] = useState('');
  const [revision, setRevision] = useState(0);
  const [review, setReview] = useState<Record<string, unknown> | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [wake, setWake] = useState(0);
  const latest = useRef(actions);
  latest.current = actions;
  const lifecycle = useRef(0);
  const phase = useRef<'idle' | 'pulling' | 'review' | 'ready' | 'blocked'>('idle');
  const saved = useRef('');
  const writing = useRef<Promise<boolean> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controller = useRef<AbortController | null>(null);
  const userId = trusted ? session?.user?.id : undefined;
  const festivalId = state.ready ? state.festival?.id : undefined;
  const key = userId && festivalId ? `${userId}:${festivalId}` : null;
  const body = JSON.stringify(actions.cloudPayload());
  const baselineKey = `cloudBaseline:${festivalId}`;

  const fail = (error: string, offline = !navigator.onLine) => {
    setStatus(offline ? 'offline' : 'failed');
    setDetail(error);
  };

  useEffect(() => {
    const version = ++lifecycle.current;
    const abort = new AbortController();
    controller.current = abort;
    saved.current = '';
    writing.current = null;
    setReview(null);
    setSavedAt(null);
    if (!key || !festivalId) {
      phase.current = 'idle';
      setStatus('idle');
      return () => { ++lifecycle.current; abort.abort(); };
    }
    phase.current = 'pulling';
    setStatus('pulling');
    setDetail('Checking the saved plan before uploading.');
    const readRevision = storage.planRevision();
    void withSessionLock(async () => {
      const identity = await api.session(abort.signal);
      if (abort.signal.aborted) return;
      if (!identity.ok || identity.data?.user?.id !== userId) {
        throw new Error('The account could not be confirmed. Sign in again or retry.');
      }
      const result = await api.readSave(festivalId, abort.signal);
      if (abort.signal.aborted || version !== lifecycle.current) return;
      if (!result.ok) throw new Error(result.error ?? 'The saved plan could not be checked.');
      const payload = result.data?.save?.payload;
      if (payload != null && (typeof payload !== 'object' || Array.isArray(payload))) {
        throw new Error('The saved plan is unreadable. No choices were changed.');
      }
      const remote = payload as Record<string, unknown> | null;
      if (remote) validateCloudPlan(remote);
      const fingerprint = remote ? effectivePlanFingerprint(remote) : '';
      const rawBaseline = await storage.meta(baselineKey);
      const baseline = baselinePlan(rawBaseline);
      if (rawBaseline != null && !baseline) throw new Error('The previous account save could not be verified. Local choices were kept.');
      const needsReview = await storage.meta<boolean>('needsDevicePlanReview');
      const origin = await storage.meta<string>('planOrigin');
      const touched = await storage.meta<boolean>('planTouched');
      if (abort.signal.aborted || version !== lifecycle.current) return;
      const localPayload = latest.current.cloudPayload();
      const ownership = origin === 'signup' || (!needsReview && baseline) ? 'owned'
        : origin === 'pristine' ? touched ? 'owned' : 'pristine' : 'unknown';
      const decision = decidePlanSync(localPayload, remote, needsReview ? null : baseline, ownership);
      await storage.setMeta(`cloudObserved:${festivalId}`, { payload: remote, savedAt: result.data?.save?.savedAt ?? null });
      if (abort.signal.aborted || version !== lifecycle.current) return;
      setSavedAt(result.data?.save?.savedAt ?? null);
      if (decision === 'paused' || decision === 'removed') {
        phase.current = 'review';
        setReview(remote);
        setStatus('review');
        setDetail(decision === 'removed'
          ? 'Saved locally. The account copy was removed; automatic account saving is paused.'
          : 'Saved locally. Account saving is paused because the copies have separate changes or their ownership is uncertain. You can keep planning.');
        return;
      }
      if (decision === 'restore') {
        if (storage.planRevision() !== readRevision) {
          phase.current = 'blocked';
          fail('New local edits arrived while checking the account. They are kept here; retry to check again.');
          return;
        }
        await latest.current.restoreCloud(remote!);
        if (abort.signal.aborted || version !== lifecycle.current) return;
      }
      saved.current = fingerprint;
      phase.current = 'ready';
      if (decision === 'equal' || decision === 'restore') {
        await storage.setMeta(baselineKey, remote);
        await storage.setMeta('needsDevicePlanReview', false);
        setStatus('saved');
        setDetail('The current plan is saved to this account.');
      } else setStatus('pending');
      setWake((value) => value + 1);
    }).catch((error: unknown) => {
      if (abort.signal.aborted || version !== lifecycle.current) return;
      phase.current = 'blocked';
      fail(error instanceof Error ? error.message : 'The saved plan could not be checked.');
    });
    return () => { ++lifecycle.current; abort.abort(); };
  }, [key, festivalId, revision, storage, baselineKey, userId]);

  const saveNow = useCallback(async (): Promise<boolean> => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (phase.current === 'blocked') { setRevision((value) => value + 1); return false; }
    if (phase.current !== 'ready' || !key || !festivalId || !userId) return false;
    if (writing.current) return writing.current;
    if (!navigator.onLine) { fail('This device is offline. Its plan is waiting to save.', true); return false; }
    const version = lifecycle.current;
    const abort = controller.current!;
    const requestRevision = storage.planRevision();
    const payload = latest.current.cloudPayload();
    const fingerprint = effectivePlanFingerprint(payload);
    setStatus('saving');
    setDetail('Saving this plan to the account.');
    const task = withSessionLock(async () => {
      if (abort.signal.aborted) return false;
      const identity = await api.session(abort.signal);
      if (abort.signal.aborted || version !== lifecycle.current) return false;
      if (!identity.ok || identity.data?.user?.id !== userId) {
        phase.current = 'blocked';
        fail('The account changed or could not be confirmed. This plan was not uploaded.');
        void refresh();
        return false;
      }
      const remote = await api.readSave(festivalId, abort.signal);
      if (abort.signal.aborted || version !== lifecycle.current) return false;
      if (!remote.ok) { fail(remote.error ?? 'The saved plan could not be checked.'); return false; }
      const remotePlan = remote.data?.save?.payload as Record<string, unknown> | null;
      if (remotePlan != null) validateCloudPlan(remotePlan);
      const remoteFingerprint = remotePlan ? effectivePlanFingerprint(remotePlan) : '';
      if (remoteFingerprint !== saved.current) {
        const baseline = baselinePlan(await storage.meta(baselineKey));
        if (abort.signal.aborted || version !== lifecycle.current) return false;
        const decision = decidePlanSync(latest.current.cloudPayload(), remotePlan, baseline, 'unknown');
        if (decision === 'equal') {
          saved.current = remoteFingerprint;
          await storage.setMeta(baselineKey, remotePlan);
          setStatus('saved');
          setDetail('The current plan is saved to this account.');
          setSavedAt(remote.data?.save?.savedAt ?? null);
          return true;
        }
        if (decision === 'restore') {
          if (storage.planRevision() !== requestRevision) { fail('New local edits arrived. Retry to check the account again.'); return false; }
          await latest.current.restoreCloud(remotePlan!);
          if (abort.signal.aborted || version !== lifecycle.current) return false;
          saved.current = remoteFingerprint;
          await storage.setMeta(baselineKey, remotePlan);
          setStatus('saved');
          setDetail('The account plan was restored; a local backup was kept.');
          setSavedAt(remote.data?.save?.savedAt ?? null);
          return true;
        }
        if (remotePlan) {
          try { validateCloudPlan(remotePlan); } catch { fail('The account copy is unreadable. Nothing was uploaded.'); return false; }
          phase.current = 'review';
          setReview(remotePlan);
          setStatus('review');
          await storage.setMeta(`cloudObserved:${festivalId}`, { payload: remotePlan, savedAt: remote.data?.save?.savedAt });
          setSavedAt(remote.data?.save?.savedAt ?? null);
          setDetail('Saved locally. The account copy also changed, so account saving is paused. You can keep planning.');
        } else {
          phase.current = 'blocked';
          fail('The account copy was removed. Retry to check it again.');
        }
        return false;
      }
      const result = await api.writeSave(festivalId, payload);
      if (abort.signal.aborted || version !== lifecycle.current) return false;
      if (!result.ok) { fail(result.error ?? 'The plan did not save.', result.status === 0 || !navigator.onLine); return false; }
      saved.current = fingerprint;
      await storage.setMeta(baselineKey, payload);
      await storage.setMeta('needsDevicePlanReview', false);
      setSavedAt(result.data?.savedAt ?? null);
      const current = effectivePlanFingerprint(latest.current.cloudPayload()) === fingerprint;
      setStatus(current ? 'saved' : 'pending');
      setDetail(current ? 'The current plan is saved to this account.' : 'Newer choices are waiting to save.');
      void refresh();
      return true;
    });
    writing.current = task;
    try { return await task; }
    catch (error) {
      if (version === lifecycle.current) fail(error instanceof Error ? error.message : 'The account save failed.');
      return false;
    }
    finally {
      if (version === lifecycle.current) {
        writing.current = null;
        // A change during the write gets a later, serialized save.
        if (saved.current === fingerprint && effectivePlanFingerprint(latest.current.cloudPayload()) !== fingerprint) {
          setWake((value) => value + 1);
        }
      }
    }
  }, [key, festivalId, userId, storage, baselineKey, refresh]);

  useEffect(() => {
    if (phase.current !== 'ready' || !key || effectivePlanFingerprint(JSON.parse(body)) === saved.current) return;
    if (writing.current) return;
    if (!navigator.onLine) { fail('This device is offline. Its plan is waiting to save.', true); return; }
    setStatus('pending');
    setDetail('The current plan will save after changes settle.');
    timer.current = setTimeout(() => void saveNow(), SETTLE_MS);
    return () => { if (timer.current) clearTimeout(timer.current); timer.current = null; };
  }, [body, key, wake, saveNow]);

  useEffect(() => {
    const online = () => { if (phase.current === 'blocked') setRevision((v) => v + 1); else void saveNow(); };
    const offline = () => { if (key) fail('This device is offline. Its plan is waiting to save.', true); };
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => { window.removeEventListener('online', online); window.removeEventListener('offline', offline); };
  }, [key, saveNow]);

  return <CloudSyncContext.Provider value={{ status, detail, saveNow, accountCopy: review, savedAt,
    recheck: () => setRevision((value) => value + 1) }}>
    {children}
  </CloudSyncContext.Provider>;
}

export function useCloudSync() {
  const value = useContext(CloudSyncContext);
  if (!value) throw new Error('useCloudSync used outside the cloud sync provider');
  return value;
}
